export type FlowSource = 'gh' | 'sp' | 'email';

export interface FlowState {
  gh: boolean;
  sp: boolean;
  email: boolean;
  /** Color of the middle document-source path. SharePoint when omitted. */
  document?: BrainPalette;
}

export interface BrainPalette {
  fill: string;
  stroke: string;
  glow: string;
  particle: [number, number, number];
}

export const DEFAULT_PALETTE: BrainPalette = {
  fill: '#A5F3E4',
  stroke: '#0D9488',
  glow: '165, 243, 228',
  particle: [165, 243, 228]
};

export const SOURCE_PALETTES: Record<FlowSource, BrainPalette> = {
  gh: {
    fill: '#C9D1D9',
    stroke: '#57606A',
    glow: '201, 209, 217',
    particle: [72, 79, 88]
  },
  sp: {
    fill: '#7EE0D6',
    stroke: '#038387',
    glow: '3, 131, 135',
    particle: [3, 131, 135]
  },
  email: {
    fill: '#93C5FD',
    stroke: '#0078D4',
    glow: '0, 120, 212',
    particle: [0, 120, 212]
  }
};

export function paletteFromSources(state: FlowState): BrainPalette {
  const active: BrainPalette[] = [];
  if (state.gh) active.push(SOURCE_PALETTES.gh);
  if (state.sp) active.push(state.document ?? SOURCE_PALETTES.sp);
  if (state.email) active.push(SOURCE_PALETTES.email);
  if (!active.length) return DEFAULT_PALETTE;
  if (active.length === 1) return active[0];
  return blendPalettes(active);
}

export function combinePalettes(list: BrainPalette[]): BrainPalette {
  if (!list.length) return SOURCE_PALETTES.sp;
  if (list.length === 1) return list[0];
  return blendPalettes(list);
}

function hexToRgb(hex: string): [number, number, number] {
  const n = hex.replace('#', '');
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16)
  ];
}

function rgbToHex(c: [number, number, number]): string {
  return '#' + c.map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
}

function blendPalettes(list: BrainPalette[]): BrainPalette {
  const fill: [number, number, number] = [0, 0, 0];
  const stroke: [number, number, number] = [0, 0, 0];
  const particle: [number, number, number] = [0, 0, 0];
  const glow: [number, number, number] = [0, 0, 0];
  for (const p of list) {
    const f = hexToRgb(p.fill);
    const s = hexToRgb(p.stroke);
    const g = p.glow.split(',').map(n => Number(n.trim())) as [number, number, number];
    for (let i = 0; i < 3; i++) {
      fill[i] += f[i];
      stroke[i] += s[i];
      particle[i] += p.particle[i];
      glow[i] += g[i];
    }
  }
  const n = list.length;
  const outFill: [number, number, number] = [fill[0] / n, fill[1] / n, fill[2] / n];
  const outStroke: [number, number, number] = [stroke[0] / n, stroke[1] / n, stroke[2] / n];
  const outParticle: [number, number, number] = [particle[0] / n, particle[1] / n, particle[2] / n];
  const outGlow: [number, number, number] = [glow[0] / n, glow[1] / n, glow[2] / n];
  return {
    fill: rgbToHex(outFill),
    stroke: rgbToHex(outStroke),
    glow: `${Math.round(outGlow[0])}, ${Math.round(outGlow[1])}, ${Math.round(outGlow[2])}`,
    particle: outParticle
  };
}

interface Pt { x: number; y: number; }

interface NodeHit { x: number; y: number; r: number; }

interface Particle {
  x: number;
  y: number;
  r: number;
  t: number;
  dur: number;
  phase: 'travel' | 'seek' | 'merge';
  source: FlowSource;
  color: [number, number, number];
  target: NodeHit | null;
  merge: number;
  wobble: number;
  wobbleSpeed: number;
  trail: Pt[];
}

interface Ripple {
  x: number;
  y: number;
  r: number;
  max: number;
  life: number;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: [number, number, number];
}

const VB_W = 540;
const VB_H = 480;
const TEAL_FILL: [number, number, number, number] = [0.647, 0.953, 0.894, 1];
const TEAL_STROKE: [number, number, number, number] = [0.051, 0.580, 0.533, 1];
const MAX_PARTICLES = 48;
const SPAWN_RATE: Record<FlowSource, number> = { gh: 2.6, sp: 3.6, email: 2.8 };

const SOURCE_COLOR: Record<FlowSource, [number, number, number]> = {
  gh: SOURCE_PALETTES.gh.particle,
  sp: SOURCE_PALETTES.sp.particle,
  email: SOURCE_PALETTES.email.particle
};

const PATHS: Record<FlowSource, [Pt, Pt, Pt, Pt]> = {
  gh:    [{ x: 40, y: 96  }, { x: 180, y: 96  }, { x: 200, y: 190 }, { x: 210, y: 208 }],
  sp:    [{ x: 40, y: 240 }, { x: 175, y: 238 }, { x: 195, y: 242 }, { x: 208, y: 240 }],
  email: [{ x: 40, y: 384 }, { x: 180, y: 384 }, { x: 200, y: 290 }, { x: 210, y: 272 }]
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function mixColor(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

function cubic(p: [Pt, Pt, Pt, Pt], t: number): Pt {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  return {
    x: uu * u * p[0].x + 3 * uu * t * p[1].x + 3 * u * tt * p[2].x + tt * t * p[3].x,
    y: uu * u * p[0].y + 3 * uu * t * p[1].y + 3 * u * tt * p[2].y + tt * t * p[3].y
  };
}

function rgb(c: [number, number, number], a = 1): string {
  return `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;
}

export function recolorBrainNetwork(data: unknown): unknown {
  const orange = 'c97a16';
  const blue = '2e8fe0';
  const white = 'ffffff';
  const black = '000000';

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    const obj = node as Record<string, unknown>;
    if ((obj['ty'] === 'fl' || obj['ty'] === 'st') && obj['c'] && typeof obj['c'] === 'object') {
      const c = obj['c'] as { k?: unknown };
      if (Array.isArray(c.k) && typeof c.k[0] === 'number') {
        const hex = (c.k as number[]).slice(0, 3)
          .map(v => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0'))
          .join('');
        if (obj['ty'] === 'fl' && (hex === orange || hex === white || hex === black)) {
          c.k = [...TEAL_FILL];
        } else if (obj['ty'] === 'st' && hex === blue) {
          c.k = [...TEAL_STROKE];
        }
      }
    }
    for (const key of Object.keys(obj)) walk(obj[key]);
  };

  walk(data);
  return data;
}

function normalizeColor(value: string): string {
  return value.replace(/\s/g, '').toLowerCase();
}

function isWhiteColor(value: string): boolean {
  const n = normalizeColor(value);
  return n === '#fff' || n === '#ffffff' || n === 'white' || n === 'rgb(255,255,255)';
}

export function applyBrainPalette(host: HTMLElement, palette: BrainPalette, resting: boolean): void {
  const svg = host.querySelector('svg');
  if (!svg) return;

  svg.querySelectorAll<SVGElement>('*').forEach(el => {
    const fillAttr = el.getAttribute('fill');
    const fill = fillAttr && fillAttr !== 'none' ? fillAttr : el.style.fill;
    if (fill && fill !== 'none') {
      el.setAttribute('fill', palette.fill);
      el.style.fill = palette.fill;
    }

    const strokeAttr = el.getAttribute('stroke');
    const stroke = strokeAttr && strokeAttr !== 'none' ? strokeAttr : el.style.stroke;
    if (!stroke || stroke === 'none') return;

    if (resting || !isWhiteColor(stroke)) {
      el.setAttribute('stroke', palette.stroke);
      el.style.stroke = palette.stroke;
    }
  });
}

export class BrainFlowEngine {
  private canvas: HTMLCanvasElement;
  private wrap: HTMLElement;
  private lottieHost: HTMLElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private ripples: Ripple[] = [];
  private sparks: Spark[] = [];
  private nodes: NodeHit[] = [];
  private state: FlowState = { gh: false, sp: false, email: false };
  private palette: BrainPalette = DEFAULT_PALETTE;
  private acc: Record<FlowSource, number> = { gh: 0, sp: 0, email: 0 };
  private raf = 0;
  private last = 0;
  private running = false;
  private reduceMotion = false;
  private resizeObs?: ResizeObserver;

  constructor(canvas: HTMLCanvasElement, wrap: HTMLElement, lottieHost: HTMLElement, reduceMotion: boolean) {
    this.canvas = canvas;
    this.wrap = wrap;
    this.lottieHost = lottieHost;
    this.reduceMotion = reduceMotion;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
    this.resize();
    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(wrap);
  }

  setState(state: FlowState): void {
    this.state = state;
    this.palette = paletteFromSources(state);
  }

  private sourceColor(source: FlowSource): [number, number, number] {
    if (source === 'sp' && this.state.document) return this.state.document.particle;
    return SOURCE_COLOR[source];
  }

  start(): void {
    if (this.running || this.reduceMotion) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(0.033, (now - this.last) / 1000);
      this.last = now;
      this.tick(dt);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.resizeObs?.disconnect();
    this.particles = [];
    this.ripples = [];
    this.sparks = [];
  }

  private resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, this.wrap.clientWidth);
    const h = Math.max(1, this.wrap.clientHeight);
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private vbToPx(p: Pt): Pt {
    return {
      x: (p.x / VB_W) * this.wrap.clientWidth,
      y: (p.y / VB_H) * this.wrap.clientHeight
    };
  }

  private sampleNodes(): void {
    const svg = this.lottieHost.querySelector('svg');
    const wrapRect = this.wrap.getBoundingClientRect();
    const next: NodeHit[] = [];
    if (svg) {
      svg.querySelectorAll('ellipse, circle').forEach(el => {
        const b = el.getBoundingClientRect();
        const r = Math.min(b.width, b.height) / 2;
        if (r < 2.6 || r > 20) return;
        next.push({
          x: b.left - wrapRect.left + b.width / 2,
          y: b.top - wrapRect.top + b.height / 2,
          r
        });
      });
    }
    this.nodes = next.length ? next : this.fallbackNodes();
  }

  private fallbackNodes(): NodeHit[] {
    const cx = (332 / VB_W) * this.wrap.clientWidth;
    const cy = (240 / VB_H) * this.wrap.clientHeight;
    const rx = 0.17 * this.wrap.clientWidth;
    const ry = 0.20 * this.wrap.clientHeight;
    const nodes: NodeHit[] = [];
    for (let i = 0; i < 22; i++) {
      const a = (Math.PI * 2 * i) / 22 + 0.2;
      const jitter = 0.55 + (i % 5) * 0.09;
      nodes.push({
        x: cx + Math.cos(a) * rx * jitter,
        y: cy + Math.sin(a) * ry * jitter,
        r: 5
      });
    }
    return nodes;
  }

  private spawn(source: FlowSource): void {
    if (this.particles.length >= MAX_PARTICLES) return;
    const origin = this.vbToPx(PATHS[source][0]);
    this.particles.push({
      x: origin.x,
      y: origin.y,
      r: 3.4 + Math.random() * 1.1,
      t: 0,
      dur: 0.85 + Math.random() * 0.35,
      phase: 'travel',
      source,
      color: this.sourceColor(source),
      target: null,
      merge: 0,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 6 + Math.random() * 4,
      trail: []
    });
  }

  private pickTarget(from: Pt): NodeHit | null {
    if (!this.nodes.length) return null;
    const ranked = this.nodes
      .map(n => ({ n, d: (n.x - from.x) ** 2 + (n.y - from.y) ** 2 }))
      .sort((a, b) => a.d - b.d);
    const pool = ranked.slice(0, Math.min(10, ranked.length));
    return pool[(Math.random() * pool.length) | 0].n;
  }

  private followNode(anchor: Pt): NodeHit | null {
    if (!this.nodes.length) return null;
    let best = this.nodes[0];
    let bestD = Infinity;
    for (const n of this.nodes) {
      const d = (n.x - anchor.x) ** 2 + (n.y - anchor.y) ** 2;
      if (d < bestD) {
        best = n;
        bestD = d;
      }
    }
    return best;
  }

  private burst(x: number, y: number, color: [number, number, number]): void {
    this.ripples.push({ x, y, r: 2, max: 18 + Math.random() * 8, life: 1 });
    for (let i = 0; i < 7; i++) {
      const a = (Math.PI * 2 * i) / 7 + Math.random() * 0.4;
      const s = 28 + Math.random() * 36;
      this.sparks.push({
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: 1,
        color
      });
    }
  }

  private tick(dt: number): void {
    this.sampleNodes();

    (['gh', 'sp', 'email'] as FlowSource[]).forEach(src => {
      if (!this.state[src]) {
        this.acc[src] = 0;
        return;
      }
      this.acc[src] += dt * SPAWN_RATE[src];
      while (this.acc[src] >= 1) {
        this.acc[src] -= 1;
        this.spawn(src);
      }
    });

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > 8) p.trail.shift();
      if (p.phase === 'travel') {
        p.t += dt / p.dur;
        const t = Math.min(1, p.t);
        const pt = this.vbToPx(cubic(PATHS[p.source], t));
        p.wobble += dt * p.wobbleSpeed;
        const amp = 4.5 * Math.sin(Math.PI * t);
        p.x = pt.x;
        p.y = pt.y + Math.sin(p.wobble) * amp;
        p.color = mixColor(this.sourceColor(p.source), this.palette.particle, t * 0.45);
        if (t >= 1) {
          p.phase = 'seek';
          p.target = this.pickTarget(p);
        }
      } else if (p.phase === 'seek') {
        if (p.target) p.target = this.followNode(p.target) ?? p.target;
        else p.target = this.pickTarget(p);
        const target = p.target;
        if (!target) {
          this.particles.splice(i, 1);
          continue;
        }
        const dx = target.x - p.x;
        const dy = target.y - p.y;
        const dist = Math.hypot(dx, dy) || 1;
        const speed = 165 + Math.min(140, dist * 0.55);
        const step = Math.min(dist, speed * dt);
        p.x += (dx / dist) * step;
        p.y += (dy / dist) * step;
        p.color = mixColor(p.color, this.palette.particle, 0.14);
        p.r = lerp(p.r, Math.max(2.2, target.r * 0.55), 0.08);
        if (dist < target.r + 3.5) {
          p.phase = 'merge';
          p.merge = 0;
        }
      } else {
        p.merge += dt / 0.18;
        if (p.target) p.target = this.followNode(p.target) ?? p.target;
        const target = p.target;
        if (target) {
          p.x = lerp(p.x, target.x, 0.45);
          p.y = lerp(p.y, target.y, 0.45);
        }
        p.r *= 0.84;
        if (p.merge >= 1) {
          this.burst(p.x, p.y, this.palette.particle);
          this.particles.splice(i, 1);
        }
      }
    }

    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const r = this.ripples[i];
      r.life -= dt * 2.4;
      r.r = lerp(2, r.max, 1 - r.life);
      if (r.life <= 0) this.ripples.splice(i, 1);
    }

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i];
      s.life -= dt * 3.2;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vx *= 0.92;
      s.vy *= 0.92;
      if (s.life <= 0) this.sparks.splice(i, 1);
    }

    this.draw();
  }

  private draw(): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.wrap.clientWidth, this.wrap.clientHeight);

    for (const r of this.ripples) {
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
      ctx.strokeStyle = rgb(this.palette.particle, 0.55 * r.life);
      ctx.lineWidth = 1.8 * r.life;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.r * 0.45, 0, Math.PI * 2);
      ctx.fillStyle = rgb(this.palette.particle, 0.18 * r.life);
      ctx.fill();
    }

    for (const s of this.sparks) {
      ctx.beginPath();
      ctx.arc(s.x, s.y, 1.3 * s.life, 0, Math.PI * 2);
      ctx.fillStyle = rgb(s.color, 0.9 * s.life);
      ctx.fill();
    }

    for (const p of this.particles) {
      if (p.trail.length > 1) {
        ctx.beginPath();
        ctx.moveTo(p.trail[0].x, p.trail[0].y);
        for (let i = 1; i < p.trail.length; i++) ctx.lineTo(p.trail[i].x, p.trail[i].y);
        ctx.lineTo(p.x, p.y);
        ctx.strokeStyle = rgb(p.color, 0.28);
        ctx.lineWidth = 1.6;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4.2);
      glow.addColorStop(0, rgb(p.color, 0.55));
      glow.addColorStop(1, rgb(p.color, 0));
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * 4.2, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = rgb(p.color, 0.95);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(p.x - p.r * 0.25, p.y - p.r * 0.28, p.r * 0.38, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fill();
    }
  }
}
